import { crsLeadSubmitted } from "@/lib/analytics-events";
import { useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

import { submitEnquiry } from "@/lib/forms.functions";
import { submissionSchema } from "@/lib/form-schema";

const SECTORS = [
  "Council",
  "Housing Association",
  "Facilities Management",
  "Main Contractor",
  "Commercial",
  "Agriculture",
] as const;

export function B2BEnquiryForm({
  idPrefix = "b2b",
  sourcePage,
  serviceOfInterest = "B2B Enquiry",
  submitLabel = "Request Feasibility Assessment",
  defaultSector,
}: {
  idPrefix?: string;
  sourcePage: string;
  serviceOfInterest?: string;
  submitLabel?: string;
  defaultSector?: string;
}) {
  const submit = useServerFn(submitEnquiry);
  const navigate = useNavigate();
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setErrors({});
    setFormError(null);

    const fd = new FormData(e.currentTarget);
    const organisation = ((fd.get("organisation") as string) || "").trim();
    const sector = ((fd.get("sector") as string) || "").trim();
    const userMessage = ((fd.get("message") as string) || "").trim();

    const composedMessage = [
      organisation ? `Organisation: ${organisation}` : null,
      sector ? `Sector: ${sector}` : null,
      userMessage ? `\n${userMessage}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    const raw = {
      form_type: "survey" as const,
      name: ((fd.get("name") as string) || "").trim() || undefined,
      phone: ((fd.get("phone") as string) || "").trim() || undefined,
      email: ((fd.get("email") as string) || "").trim() || undefined,
      postcode: ((fd.get("postcode") as string) || "").trim() || undefined,
      service_of_interest: serviceOfInterest,
      message: composedMessage || undefined,
      source_page: sourcePage,
      honeypot: (fd.get("company_website") as string) || "",
    };

    if (!organisation) {
      setErrors({ organisation: "Organisation is required" });
      setStatus("idle");
      return;
    }
    if (!sector) {
      setErrors({ sector: "Please select a sector" });
      setStatus("idle");
      return;
    }

    const parsed = submissionSchema.safeParse(raw);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[String(issue.path[0] ?? "form")] = issue.message;
      }
      setErrors(fieldErrors);
      setStatus("idle");
      return;
    }

    try {
      const result = await submit({ data: parsed.data });
      if (result.success) {
        if (result.id) crsLeadSubmitted({ leadId: result.id, formId: "b2b_enquiry", leadType: "b2b" });
        toast.success("Enquiry sent", {
          description: "Thanks - the commercial team will be in touch shortly.",
        });
        navigate({ to: "/thank-you" });
      } else {
        setFormError(result.error ?? "Something went wrong. Please try again.");
        setStatus("error");
      }
    } catch (err) {
      console.error(err);
      setFormError("Network error. Please call us on 0800 009 2992.");
      setStatus("error");
    }
  }

  const labelCls = "block text-sm font-semibold mb-1.5";
  const inputCls =
    "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-ring";
  const errCls = "text-xs text-destructive mt-1";

  return (
    <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-3 sm:gap-4" noValidate>
      <input
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden
      />

      <div>
        <label htmlFor={`${idPrefix}-name`} className={labelCls}>
          Name <span className="text-destructive">*</span>
        </label>
        <input id={`${idPrefix}-name`} name="name" required className={inputCls} />
        {errors.name && <p className={errCls}>{errors.name}</p>}
      </div>
      <div>
        <label htmlFor={`${idPrefix}-org`} className={labelCls}>
          Organisation <span className="text-destructive">*</span>
        </label>
        <input id={`${idPrefix}-org`} name="organisation" required className={inputCls} />
        {errors.organisation && <p className={errCls}>{errors.organisation}</p>}
      </div>
      <div className="sm:col-span-2">
        <label htmlFor={`${idPrefix}-sector`} className={labelCls}>
          Sector <span className="text-destructive">*</span>
        </label>
        <select id={`${idPrefix}-sector`} name="sector" required defaultValue={defaultSector ?? ""} className={inputCls}>
          <option value="" disabled>
            Select sector
          </option>
          {SECTORS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {errors.sector && <p className={errCls}>{errors.sector}</p>}
      </div>
      <div>
        <label htmlFor={`${idPrefix}-phone`} className={labelCls}>
          Phone <span className="text-destructive">*</span>
        </label>
        <input id={`${idPrefix}-phone`} name="phone" required inputMode="tel" className={inputCls} />
        {errors.phone && <p className={errCls}>{errors.phone}</p>}
      </div>
      <div>
        <label htmlFor={`${idPrefix}-email`} className={labelCls}>
          Email <span className="text-destructive">*</span>
        </label>
        <input id={`${idPrefix}-email`} name="email" required type="email" className={inputCls} />
        {errors.email && <p className={errCls}>{errors.email}</p>}
      </div>
      <div className="sm:col-span-2">
        <label htmlFor={`${idPrefix}-postcode`} className={labelCls}>
          Site postcode <span className="text-destructive">*</span>
        </label>
        <input id={`${idPrefix}-postcode`} name="postcode" required className={inputCls} />
        {errors.postcode && <p className={errCls}>{errors.postcode}</p>}
      </div>
      <div className="sm:col-span-2">
        <label htmlFor={`${idPrefix}-msg`} className={labelCls}>
          Anything we should know? (optional)
        </label>
        <textarea id={`${idPrefix}-msg`} name="message" rows={3} className={inputCls} />
      </div>

      {formError && (
        <div className="sm:col-span-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {formError}
        </div>
      )}

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors w-full disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Sending...
            </>
          ) : (
            <>
              {submitLabel} <ArrowRight className="h-4 w-4" aria-hidden />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
